// Every secondary dialog the v1 appointment modal owned, kept in one place.

import type { FC } from 'react'

import { ModalAction } from 'common/components/Modal'
import { BUTTON_CONSTANTS, BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import { MODAL_LABELS } from 'common/constants/modalConstants'
import { INFO_CONSTANTS } from 'features/Home/constants/infoConstants'
import type { IOption } from 'common/interfaces/IOption'
import { Warning } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/Warning/Warning'
import { PATIENTS_LABELS } from 'features/Patients/constants/patientsConstants'
import type { IPatient } from 'features/Patients/interfaces/IPatient'
import {
  PatientFormModal,
  type PatientFormType,
} from 'features/Patients/components/PatientFormModal/PatientFormModal'
import { EncounterSelectModal } from 'features/Profile/features/TransactionsMapping/EncounterSelectModal'

import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog'
import { PatientSearchModal } from '../PatientSearchModal/PatientSearchModal'

type Props = {
  languages: IOption[]
  isAddPatientOpen: boolean
  isPatientSearchOpen: boolean
  isNotifyOpen: boolean
  isManualCheckInOpen: boolean
  isDeleteOpen: boolean
  isDeleting: boolean
  isEncounterOpen: boolean
  isResolvingEncounter: boolean
  encounterPatientId?: number
  selectedEncounterId: number | null
  encounterError?: string
  overlapConfirmOpen: boolean
  awayConfirmOpen: boolean
  onCloseAddPatient: () => void
  onSubmitPatient: (patient: PatientFormType) => Promise<void>
  onClosePatientSearch: () => void
  onSelectPatient: (patient: IPatient) => void
  onCloseNotify: () => void
  onConfirmNotify: () => void
  onCloseManualCheckIn: () => void
  onConfirmManualCheckIn: () => void
  onCancelDelete: () => void
  onConfirmDelete: (sendSms: boolean) => Promise<void>
  onCloseEncounter: () => void
  onConfirmEncounter: (params: { encounterId: number; encounterUuid: string }) => void
  onCloseOverlapConfirm: () => void
  onConfirmOverlap: () => void
  onCloseAwayConfirm: () => void
  onConfirmAway: () => void
}

export const EhrAppointmentModals: FC<Props> = ({
  languages,
  isAddPatientOpen,
  isPatientSearchOpen,
  isNotifyOpen,
  isManualCheckInOpen,
  isDeleteOpen,
  isDeleting,
  isEncounterOpen,
  isResolvingEncounter,
  encounterPatientId,
  selectedEncounterId,
  encounterError,
  overlapConfirmOpen,
  awayConfirmOpen,
  onCloseAddPatient,
  onSubmitPatient,
  onClosePatientSearch,
  onSelectPatient,
  onCloseNotify,
  onConfirmNotify,
  onCloseManualCheckIn,
  onConfirmManualCheckIn,
  onCancelDelete,
  onConfirmDelete,
  onCloseEncounter,
  onConfirmEncounter,
  onCloseOverlapConfirm,
  onConfirmOverlap,
  onCloseAwayConfirm,
  onConfirmAway,
}) => (
  <>
    <PatientFormModal
      isOpen={isAddPatientOpen}
      onClose={onCloseAddPatient}
      onSubmit={onSubmitPatient}
      languages={languages}
    />

    {isPatientSearchOpen && (
      <PatientSearchModal
        isOpen={isPatientSearchOpen}
        onClose={onClosePatientSearch}
        onSelectPatient={onSelectPatient}
      />
    )}

    <Warning
      open={overlapConfirmOpen}
      title={INFO_CONSTANTS.ARE_YOU_SURE}
      warning={INFO_CONSTANTS.MULTIPLE_PATIENTS_TITLE}
      okButtonText={INFO_CONSTANTS.YES}
      handleClose={onCloseOverlapConfirm}
      handleConfirm={onConfirmOverlap}
      hasCancel
    />

    <Warning
      open={awayConfirmOpen}
      title={INFO_CONSTANTS.DOCTOR_AWAY_TITLE}
      warning={INFO_CONSTANTS.DOCTOR_AWAY_WARNING}
      okButtonText={INFO_CONSTANTS.CONTINUE}
      handleClose={onCloseAwayConfirm}
      handleConfirm={onConfirmAway}
      hasCancel
    />

    <ModalAction
      destroyOnHidden
      isOpen={isNotifyOpen}
      title={MODAL_LABELS.NOTIFY_PATIENT}
      confirmText={BUTTON_CONSTANTS.SEND}
      cancelText={BUTTON_CONSTANTS.CANCEL}
      onConfirm={onConfirmNotify}
      onClose={onCloseNotify}
      description={PATIENTS_LABELS.CONFIRM_NOTIFY}
      confirmButtonSeverity={BUTTON_SEVERITY.SUCCESS_FILLED}
    />

    <ModalAction
      destroyOnHidden
      isOpen={isManualCheckInOpen}
      title={MODAL_LABELS.MANUAL_CHECK_IN}
      confirmText={BUTTON_CONSTANTS.YES}
      cancelText={BUTTON_CONSTANTS.CANCEL}
      onConfirm={onConfirmManualCheckIn}
      onClose={onCloseManualCheckIn}
      description={PATIENTS_LABELS.CONFIRM_MANUAL_CHECK_IN}
      confirmButtonSeverity={BUTTON_SEVERITY.SUCCESS_FILLED}
    />

    <DeleteConfirmationDialog
      isOpen={isDeleteOpen}
      onConfirm={onConfirmDelete}
      onCancel={onCancelDelete}
      isLoading={isDeleting}
    />

    <EncounterSelectModal
      isOpen={isEncounterOpen}
      onClose={onCloseEncounter}
      patientId={encounterPatientId}
      selectedEncounterId={selectedEncounterId}
      errorMessage={encounterError}
      isResolving={isResolvingEncounter}
      confirmButtonText={BUTTON_CONSTANTS.SAVE}
      onConfirm={onConfirmEncounter}
    />
  </>
)
